import { supabase } from "../supabaseClient";
import { PROBIODELIGHTS_CUSTOMER_LAUNCH_ENABLED } from "../config/featureFlags";

const isCustomerVisibleProduct = (product) =>
  PROBIODELIGHTS_CUSTOMER_LAUNCH_ENABLED || product.business_line !== "ProBioDelights";

const PRODUCT_COLUMNS = [
  "id",
  "name",
  "slug",
  "description",
  "price",
  "image_url",
  "category",
  "business_line",
  "stock",
  "featured",
  "is_active",
  "created_at",
  "updated_at",
].join(", ");

const PRODUCT_WITH_SIZES_COLUMNS = `${PRODUCT_COLUMNS}, product_sizes (id, product_id, size_name, price, created_at)`;

const normalizeStorefrontProduct = (product = {}) => {
  const slug = String(product.slug || "").trim();
  const name = String(product.name || "").trim();
  const description = String(product.description || "").trim();
  const image = String(product.image_url || "").trim();
  const category = String(product.category || "").trim();
  const businessLine = String(product.business_line || "Pantry").trim() || "Pantry";
  const price = Number(product.price) || 0;
  const stock = Number(product.stock) || 0;

  const sizes = Array.isArray(product.product_sizes) ? product.product_sizes : [];
  const options = sizes.length
    ? sizes.map((size) => ({
        id: size.id,
        productId: product.id,
        label: String(size.size_name || "").trim(),
        price: Number(size.price) || 0,
        description: "",
      })).filter((size) => size.label)
    : [{
        id: `${product.id || slug}-standard`,
        productId: product.id,
        label: "Standard",
        price,
        description: "",
      }];

  return {
    id: product.id,
    slug,
    name,
    title: name,
    description,
    image,
    category,
    business_line: businessLine,
    stock,
    featured: Boolean(product.featured),
    is_active: Boolean(product.is_active),
    price,
    options,
    created_at: product.created_at,
    updated_at: product.updated_at,
  };
};

export const getActiveProducts = async ({ category, businessLine, featuredOnly = false, search } = {}) => {
  const { data, error } = await supabase
    .from("products")
    .select(PRODUCT_WITH_SIZES_COLUMNS)
    .eq("is_active", true)
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }

  const normalizedCategory = String(category || "").trim().toLowerCase();
  const normalizedBusinessLine = String(businessLine || "").trim().toLowerCase();
  const normalizedSearch = String(search || "").trim().toLowerCase();

  return (data || []).map(normalizeStorefrontProduct).filter((product) => {
    if (!isCustomerVisibleProduct(product)) return false;
    const matchesCategory = !normalizedCategory || product.category.toLowerCase() === normalizedCategory;
    const matchesBusinessLine = !normalizedBusinessLine || product.business_line.toLowerCase() === normalizedBusinessLine;
    const matchesFeatured = !featuredOnly || product.featured;
    const searchableText = `${product.name} ${product.slug} ${product.description} ${product.category}`.toLowerCase();
    return matchesCategory && matchesBusinessLine && matchesFeatured && (!normalizedSearch || searchableText.includes(normalizedSearch));
  });
};

export const getActiveProductsByIds = async (productIds = []) => {
  const normalizedIds = [...new Set(
    (Array.isArray(productIds) ? productIds : [])
      .map((productId) => String(productId || "").trim())
      .filter(Boolean)
  )];

  if (!normalizedIds.length) {
    return [];
  }

  const { data, error } = await supabase
    .from("products")
    .select(PRODUCT_WITH_SIZES_COLUMNS)
    .eq("is_active", true)
    .in("id", normalizedIds)
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }

  return (data || []).map(normalizeStorefrontProduct).filter(isCustomerVisibleProduct);
};

export const getProductCategories = async () => {
  const products = await getActiveProducts();
  return [...new Set(products.map((product) => product.category).filter(Boolean))].sort((a, b) => a.localeCompare(b));
};

export const getProductBySlug = async (slug) => {
  if (!slug) {
    return null;
  }

  const { data, error } = await supabase
    .from("products")
    .select(PRODUCT_WITH_SIZES_COLUMNS)
    .eq("slug", slug)
    .eq("is_active", true)
    .single();

  if (error) {
    if (error.code === "PGRST116" || error.code === "PGRST116") {
      return null;
    }
    throw error;
  }

  if (!data) {
    return null;
  }

  const product = normalizeStorefrontProduct(data);
  return isCustomerVisibleProduct(product) ? product : null;
};
